import * as THREE from 'three';
import { PLANET_SKY, SKY_RADIUS, dirFromRaDec } from './sky';

// The planets up close: each one a quad on the sky sphere, at its real angular size and
// with its real orientation that night — the pole tilted on the sky by P, Saturn's rings
// opened by B (both from planets.ts). Drawn in the page's palette, like everything else.
// The detail is generic (belts and zones, ring divisions at their real radii), not a
// photograph of that night: no Great Red Spot, no moons, since where they would be is
// not computed.
//
// They only fade in once a disc is a few pixels across; until then the planet is the
// steady point in the star layer.

const LOOK_INDEX = { rocky: 0, plain: 1, banded: 2, ringed: 3 } as const;
const FLATTENING: Record<string, number> = { jupiter: 0.065, saturn: 0.098 };

const VERTEX = `
  varying vec2 vUv;
  void main() {
    vUv = uv * 2.0 - 1.0;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }`;

// vUv spans the quad in units of `uExtent` planet radii, east → x, north → y.
const FRAGMENT = `
  uniform vec3 uInk;
  uniform float uOpacity;
  uniform float uExtent;
  uniform float uLook;
  uniform float uPole;     // P, radians: the pole's angle from north through east
  uniform float uTilt;     // B, radians: the latitude facing us (and how open the rings are)
  uniform float uFlat;
  uniform float uSeed;
  varying vec2 vUv;

  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7)) + uSeed) * 43758.5453); }
  float noise(vec2 p) {
    vec2 i = floor(p); vec2 f = fract(p); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1, 0)), f.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), f.x), f.y);
  }

  // Saturn's rings by radius (planet radii): C, B, the Cassini division, A, the Encke gap.
  float rings(float r) {
    float c = smoothstep(1.23, 1.26, r) * (1.0 - smoothstep(1.51, 1.53, r)) * 0.22;
    float b = smoothstep(1.52, 1.56, r) * (1.0 - smoothstep(1.93, 1.95, r)) * (0.75 + 0.25 * sin(r * 60.0));
    float a = smoothstep(2.02, 2.04, r) * (1.0 - smoothstep(2.25, 2.27, r)) * 0.55;
    float encke = 1.0 - smoothstep(0.004, 0.0, abs(r - 2.214)) * 0.8;
    return (c + b + a) * encke;
  }

  void main() {
    vec2 q = vUv * uExtent;
    // into the planet's own frame: y along its projected north pole
    vec2 pole = vec2(sin(uPole), cos(uPole));
    vec2 eq = vec2(pole.y, -pole.x);
    vec2 p = vec2(dot(q, eq), dot(q, pole));
    float squash = 1.0 - uFlat * (1.0 - abs(sin(uTilt))); // the globe's polar flattening, as seen
    float r = length(vec2(p.x, p.y / squash));

    float light = 0.0;
    if (r < 1.0) {
      float mu = sqrt(1.0 - r * r);
      float limb = 0.3 + 0.7 * pow(mu, 0.55);
      // latitude on the globe, roughly, for the bands
      float lat = asin(clamp(p.y / squash * cos(uTilt) + mu * sin(uTilt), -1.0, 1.0));
      float tone = 1.0;
      if (uLook > 1.5) {
        float belts = sin(lat * 9.0 + 0.6) * 0.5 + 0.5;
        float wobble = noise(vec2(p.x * 3.0, lat * 22.0)) * 0.35;
        tone = mix(0.62, 1.0, smoothstep(0.25, 0.75, belts + wobble - 0.17));
        if (uLook > 2.5) tone = mix(1.0, tone, 0.45); // Saturn's bands are faint
        tone *= 1.0 - 0.25 * smoothstep(1.1, 1.45, abs(lat)); // darker poles
      } else if (uLook < 0.5) {
        tone = 0.7 + 0.3 * noise(p * 5.0); // rocky: mottled
      } else {
        tone = 0.92 + 0.08 * cos(lat * 2.0); // plain: a hint of a cap and band
      }
      light = limb * tone;
    }

    if (uLook > 2.5 && abs(sin(uTilt)) > 0.003) {
      // the ring plane seen opened by B: a ring point at radius ρ, angle φ lands on
      // (ρ cos φ, ρ sin φ sin B); its near half (toward us) is the one with p.y on B's side
      float s = sin(uTilt);
      float rho = length(vec2(p.x, p.y / s));
      float ring = rings(rho) * (0.55 + 0.45 * abs(s) * 4.0);
      bool near = p.y * s > 0.0;
      if (r >= 1.0 || near) light = mix(light, 0.82, clamp(ring, 0.0, 1.0));
      // the globe's shadow across the far rings, and the rings' across the globe
      if (r < 1.0 && near) light *= 1.0 - 0.15 * ring;
    }

    float a = light * uOpacity;
    if (a < 0.002) discard;
    // kept under the bloom's threshold, so the detail stays crisp
    gl_FragColor = vec4(mix(uInk, vec3(1.0), 0.45) * 0.55 * a, 1.0);
  }`;

export type PlanetDiscs = {
  group: THREE.Group;
  // Fade each disc by how many pixels its globe spans; returns the largest radius in px.
  update: (radiusPx: (id: string) => number) => number;
  // The disc's full extent (rings included), in degrees from its centre.
  extent: (id: string) => number;
  dispose: () => void;
};

export function createPlanetDiscs(ink: string): PlanetDiscs {
  const group = new THREE.Group();
  const meshes = new Map<string, THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>>();
  const extents = new Map<string, number>();
  const color = new THREE.Color(ink);

  PLANET_SKY.forEach((planet, i) => {
    const extentRadii = (planet.rings ? planet.rings[1] / planet.radiusKm : 1) * 1.08;
    const extentDeg = planet.angularRadius * extentRadii;
    extents.set(planet.id, extentDeg);
    // the quad's own east and north, as world vectors tangent to the sky there. It sits on
    // the sky sphere itself, exactly where its point and the aim are: even a 1% step inward
    // shows as parallax at this magnification (the camera is 350 m off the centre).
    const center = planet.dir.clone().multiplyScalar(SKY_RADIUS);
    const north = dirFromRaDec(planet.ra, planet.dec + 0.01).sub(planet.dir).normalize();
    const east = dirFromRaDec(planet.ra + 0.01 / Math.cos(THREE.MathUtils.degToRad(planet.dec)), planet.dec)
      .sub(planet.dir)
      .normalize();
    const half = SKY_RADIUS * Math.tan(THREE.MathUtils.degToRad(extentDeg));
    const geometry = new THREE.PlaneGeometry(2, 2);
    // lay the plane out in the (east, north) basis around its centre
    const position = geometry.getAttribute('position') as THREE.BufferAttribute;
    for (let v = 0; v < position.count; v++) {
      const x = position.getX(v);
      const y = position.getY(v);
      const p = center.clone().addScaledVector(east, x * half).addScaledVector(north, y * half);
      position.setXYZ(v, p.x, p.y, p.z);
    }
    position.needsUpdate = true;
    geometry.computeBoundingSphere();
    const material = new THREE.ShaderMaterial({
      uniforms: {
        uInk: { value: color },
        uOpacity: { value: 0 },
        uExtent: { value: extentRadii },
        uLook: { value: LOOK_INDEX[planet.look] },
        uPole: { value: THREE.MathUtils.degToRad(planet.poleAngle) },
        uTilt: { value: THREE.MathUtils.degToRad(planet.tilt) },
        uFlat: { value: FLATTENING[planet.id] ?? 0 },
        uSeed: { value: i * 17.3 },
      },
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.renderOrder = -3;
    mesh.frustumCulled = false;
    mesh.visible = false;
    meshes.set(planet.id, mesh);
    group.add(mesh);
  });

  return {
    group,
    update: (radiusPx) => {
      let largest = 0;
      for (const [id, mesh] of meshes) {
        const px = radiusPx(id);
        largest = Math.max(largest, px);
        const opacity = THREE.MathUtils.smoothstep(px, 1.5, 4);
        mesh.material.uniforms.uOpacity.value = opacity;
        mesh.visible = opacity > 0.001;
      }
      return largest;
    },
    extent: (id) => extents.get(id) ?? 0,
    dispose: () => {
      for (const mesh of meshes.values()) {
        mesh.geometry.dispose();
        mesh.material.dispose();
      }
    },
  };
}
